import React from 'react';
import {
  Box,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Heading,
  Tag,
  Wrap,
  WrapItem,
  Tooltip,
  VStack,
  HStack,
  Badge,
} from '@chakra-ui/react';
import { FaShieldAlt, FaCheck } from 'react-icons/fa';

const FrameworkMappings = () => {
  const nistControlFamilies = [
    {
      id: 'AC',
      name: 'Access Control',
      categories: ['authentication', 'idaas', 'privilegedAccessManagement', 'zeroTrustArchitecture']
    },
    {
      id: 'AU',
      name: 'Audit and Accountability',
      categories: ['siem', 'securityAnalytics']
    },
    {
      id: 'AT',
      name: 'Awareness and Training',
      categories: ['grcAndSecurityAwareness']
    },
    {
      id: 'CM',
      name: 'Configuration Management',
      categories: ['cloudSecurity', 'applicationSecurity']
    },
    {
      id: 'CP',
      name: 'Contingency Planning',
      categories: ['dataSecurityEncryption', 'dataSecurityDLP']
    },
    {
      id: 'IA',
      name: 'Identification and Authentication',
      categories: ['authentication', 'idaas', 'consumerIdentity']
    },
    {
      id: 'IR',
      name: 'Incident Response',
      categories: ['securityIncidentResponse', 'threatIntelligence', 'siem']
    },
    {
      id: 'MA',
      name: 'Maintenance',
      categories: ['remoteMonitoringAndManagement']
    },
    {
      id: 'MP',
      name: 'Media Protection',
      categories: ['dataSecurityEncryption', 'dataSecurityDLP']
    },
    {
      id: 'PS',
      name: 'Personnel Security',
      categories: ['grcAndSecurityAwareness']
    },
    {
      id: 'PE',
      name: 'Physical and Environmental Protection',
      categories: ['iotSecurity']
    },
    {
      id: 'PL',
      name: 'Planning',
      categories: ['riskAssessmentAndVisibility', 'riskQuantification']
    },
    {
      id: 'RA',
      name: 'Risk Assessment',
      categories: ['riskAssessmentAndVisibility', 'penTestingAndBreachSimulation']
    },
    {
      id: 'CA',
      name: 'Security Assessment and Authorization',
      categories: ['riskAssessmentAndVisibility', 'penTestingAndBreachSimulation']
    },
    {
      id: 'SC',
      name: 'System and Communications Protection',
      categories: ['networkAndInfrastructureSecurity', 'webAndInfrastructureSecurity']
    },
    {
      id: 'SI',
      name: 'System and Information Integrity',
      categories: ['endpointSecurity', 'applicationSecurity', 'extendedDetectionAndResponse']
    },
    {
      id: 'SA',
      name: 'System and Services Acquisition',
      categories: ['supplyChainSecurity']
    }
  ];

  const nistCsf = [
    {
      function: 'Identify',
      categories: ['riskAssessmentAndVisibility', 'riskQuantification', 'securityAnalytics']
    },
    {
      function: 'Protect',
      categories: [
        'networkAndInfrastructureSecurity',
        'endpointSecurity',
        'applicationSecurity',
        'dataSecurityEncryption',
        'authentication'
      ]
    },
    {
      function: 'Detect',
      categories: ['siem', 'threatIntelligence', 'securityAnalytics', 'extendedDetectionAndResponse']
    },
    {
      function: 'Respond',
      categories: ['securityIncidentResponse', 'threatIntelligence']
    },
    {
      function: 'Recover',
      categories: ['securityIncidentResponse', 'dataSecurityEncryption']
    }
  ];

  const mitreTactics = [
    {
      tactic: 'Initial Access',
      categories: ['networkAndInfrastructureSecurity', 'webAndInfrastructureSecurity', 'zeroTrustArchitecture']
    },
    {
      tactic: 'Execution',
      categories: ['endpointSecurity', 'applicationSecurity']
    },
    {
      tactic: 'Persistence',
      categories: ['endpointSecurity', 'privilegedAccessManagement']
    },
    {
      tactic: 'Privilege Escalation',
      categories: ['privilegedAccessManagement', 'authentication']
    },
    {
      tactic: 'Defense Evasion',
      categories: ['endpointSecurity', 'extendedDetectionAndResponse']
    },
    {
      tactic: 'Credential Access',
      categories: ['authentication', 'privilegedAccessManagement']
    },
    {
      tactic: 'Discovery',
      categories: ['securityAnalytics', 'threatIntelligence']
    },
    {
      tactic: 'Lateral Movement',
      categories: ['networkAndInfrastructureSecurity', 'zeroTrustArchitecture']
    },
    {
      tactic: 'Collection',
      categories: ['dataSecurityDLP', 'dataPrivacy']
    },
    {
      tactic: 'Command and Control',
      categories: ['networkAndInfrastructureSecurity', 'threatIntelligence']
    },
    {
      tactic: 'Exfiltration',
      categories: ['dataSecurityDLP', 'networkAndInfrastructureSecurity']
    },
    {
      tactic: 'Impact',
      categories: ['securityIncidentResponse', 'riskQuantification']
    }
  ];

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="md" mb={4}>Security Framework Mappings</Heading>
          <Text>
            Map security solutions to common frameworks and standards to help identify relevant tools and technologies
            for your security program.
          </Text>
        </Box>

        <Accordion allowMultiple>
          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <HStack>
                    <FaShieldAlt />
                    <Text fontWeight="bold">NIST SP 800-53 Rev. 5 Control Families</Text>
                  </HStack>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Control Family</Th>
                    <Th>Relevant Solution Categories</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {nistControlFamilies.map((family) => (
                    <Tr key={family.id}>
                      <Td>
                        <Tooltip label={`${family.id} - ${family.name}`}>
                          <Text fontWeight="bold">{family.name}</Text>
                        </Tooltip>
                      </Td>
                      <Td>
                        <Wrap>
                          {family.categories.map((category) => (
                            <WrapItem key={category}>
                              <Badge colorScheme="blue" px={2} py={1}>
                                {category.split(/(?=[A-Z])/).join(' ')}
                              </Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <HStack>
                    <FaShieldAlt />
                    <Text fontWeight="bold">NIST Cybersecurity Framework (CSF)</Text>
                  </HStack>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Function</Th>
                    <Th>Relevant Solution Categories</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {nistCsf.map((item) => (
                    <Tr key={item.function}>
                      <Td>
                        <Text fontWeight="bold">{item.function}</Text>
                      </Td>
                      <Td>
                        <Wrap>
                          {item.categories.map((category) => (
                            <WrapItem key={category}>
                              <Badge colorScheme="green" px={2} py={1}>
                                {category.split(/(?=[A-Z])/).join(' ')}
                              </Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <HStack>
                    <FaShieldAlt />
                    <Text fontWeight="bold">MITRE ATT&CK Framework</Text>
                  </HStack>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Tactic</Th>
                    <Th>Relevant Solution Categories</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {mitreTactics.map((item) => (
                    <Tr key={item.tactic}>
                      <Td>
                        <Text fontWeight="bold">{item.tactic}</Text>
                      </Td>
                      <Td>
                        <Wrap>
                          {item.categories.map((category) => (
                            <WrapItem key={category}>
                              <Badge colorScheme="purple" px={2} py={1}>
                                {category.split(/(?=[A-Z])/).join(' ')}
                              </Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </VStack>
    </Box>
  );
};

export default FrameworkMappings;